import React, { useState, useEffect } from 'react';
import { 
  CurrencyDollarIcon,
  ChartBarIcon,
  DocumentArrowDownIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import apiClient from '../../utils/api';

interface FinancialSummary {
  total_income: number;
  total_expenses: number;
  total_location_expenses: number;
  gross_profit: number;
  net_profit: number;
  profit_margin: number;
  roi: number;
}

interface ExpenseBreakdown {
  expense_type: 'feed' | 'medicine' | 'salary' | 'maintenance' | 'diesel' | 'construction' | 'misc';
  total_amount: number;
  percentage: number;
  pond_breakdown: Array<{
    pond_id: string;
    pond_name: string;
    amount: number;
  }>;
}

interface PondProfitability {
  pond_id: string;
  pond_name: string;
  pond_type: 'nursery' | 'grower';
  total_income: number;
  total_expenses: number;
  allocated_costs: number;
  direct_costs: number;
  allocated_location_costs: number;
  total_location_expenses: number;
  allocation_proportion: number;
  net_profit: number;
  profit_margin: number;
  roi: number;
  biomass_kg: number;
  cost_per_kg: number;
  revenue_per_kg: number;
}

interface FinancialReport {
  location_id: string;
  location_name: string;
  period_start: string;
  period_end: string;
  summary: FinancialSummary;
  expense_breakdown: ExpenseBreakdown[];
  pond_profitability: PondProfitability[];
  monthly_trends: Array<{
    month: string;
    income: number;
    expenses: number;
    profit: number;
  }>;
}

const FinancialReports: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [report, setReport] = useState<FinancialReport | null>(null);

  useEffect(() => {
    // Set default date range (January 1st of current year to today)
    const today = new Date();
    const currentYear = today.getFullYear();
    
    // Create January 1st in IST (UTC+5:30)
    const januaryFirst = new Date(`${currentYear}-01-01T00:00:00+05:30`);
    
    // Format dates for input fields (YYYY-MM-DD)
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    setEndDate(formatDate(today));
    setStartDate(formatDate(januaryFirst));
  }, []);

  const extractErrorMessage = (err: any): string => {
    const detail = err?.response?.data?.detail;
    if (!detail) return err?.message || 'Something went wrong';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map((d: any) => d?.msg || JSON.stringify(d)).join('; ');
    }
    if (typeof detail === 'object') {
      return detail.msg || JSON.stringify(detail);
    }
    return String(detail);
  };

  const fetchFinancialReport = async () => {
    if (!selectedLocation || !startDate || !endDate) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get('/reports/financials', {
        params: {
          location_id: selectedLocation.id,
          start: startDate,
          end: endDate,
        },
      });
      setReport(response.data);
    } catch (err: any) {
      setError(extractErrorMessage(err) || 'Failed to load financial report');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = async () => {
    if (!selectedLocation || !startDate || !endDate) return;
    
    try {
      const response = await apiClient.get('/reports/export/financials', {
        params: {
          location_id: selectedLocation.id,
          start: startDate,
          end: endDate,
        },
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `financial_report_${startDate}_${endDate}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const getProfitColor = (profit: number) => {
    if (profit > 0) return 'text-green-600';
    if (profit < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getProfitIcon = (profit: number) => {
    if (profit > 0) return <ArrowTrendingUpIcon className="h-5 w-5 text-green-600" />;
    if (profit < 0) return <ArrowTrendingDownIcon className="h-5 w-5 text-red-600" />;
    return <ChartBarIcon className="h-5 w-5 text-gray-600" />;
  };

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <CurrencyDollarIcon className="h-16 w-16 text-orange-600 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Financial Reports</h1>
        <p className="text-lg text-gray-600">
          Profit & Loss Analysis and Financial Performance
        </p>
      </div>

      {/* Date Range Selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchFinancialReport}
              disabled={loading || !startDate || !endDate}
              className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <CalendarIcon className="h-4 w-4" />
              {loading ? 'Loading...' : 'Generate Report'}
            </button>
            {report && (
              <button
                onClick={exportToCSV}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <DocumentArrowDownIcon className="h-4 w-4" />
                Export CSV
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {/* Financial Summary */}
      {report && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Financial Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(report.summary.total_income)}
              </div>
              <div className="text-sm text-gray-600">Total Income</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {formatCurrency(report.summary.total_expenses)}
              </div>
              <div className="text-sm text-gray-600">Total Expenses</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {formatCurrency(report.summary.total_location_expenses)}
              </div>
              <div className="text-sm text-gray-600">Location Costs</div>
              <div className="text-xs text-gray-500">Shared infrastructure</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${getProfitColor(report.summary.net_profit)}`}>
                {formatCurrency(report.summary.net_profit)}
              </div>
              <div className="text-sm text-gray-600">Net Profit</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${getProfitColor(report.summary.profit_margin)}`}>
                {formatPercentage(report.summary.profit_margin)}
              </div>
              <div className="text-sm text-gray-600">Profit Margin</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {report.pond_profitability.length}
              </div>
              <div className="text-sm text-gray-600">Active Ponds</div>
            </div>
          </div>
        </div>
      )}

      {/* Expense Breakdown */}
      {report && report.expense_breakdown.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Expense Breakdown</h2>
          <div className="space-y-4">
            {report.expense_breakdown.map((expense, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-medium text-gray-900 capitalize">
                    {expense.expense_type.replace('_', ' ')}
                  </h3>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-gray-900">
                      {formatCurrency(expense.total_amount)}
                    </div>
                    <div className="text-sm text-gray-600">
                      {formatPercentage(expense.percentage)} of total
                    </div>
                  </div>
                </div>
                {expense.pond_breakdown.length > 0 && (
                  <div className="mt-3">
                    <div className="text-sm text-gray-600 mb-2">Pond Breakdown:</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {expense.pond_breakdown.map((pond, pondIndex) => (
                        <div key={pondIndex} className="flex justify-between text-sm">
                          <span className="text-gray-700">{pond.pond_name}</span>
                          <span className="font-medium">{formatCurrency(pond.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pond Profitability */}
      {report && report.pond_profitability.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Pond Profitability</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pond
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Income
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expenses
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location Costs
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Allocation %
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Net Profit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Margin
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ROI
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {report.pond_profitability.map((pond, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {pond.pond_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                      {pond.pond_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {formatCurrency(pond.total_income)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                      {formatCurrency(pond.total_expenses)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-600">
                      <div>
                        <div className="font-medium">{formatCurrency(pond.allocated_location_costs)}</div>
                        <div className="text-xs text-gray-500">of {formatCurrency(pond.total_location_expenses)}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <span className="font-medium">{pond.allocation_proportion.toFixed(1)}%</span>
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-purple-600 h-2 rounded-full" 
                            style={{ width: `${Math.min(pond.allocation_proportion, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        {getProfitIcon(pond.net_profit)}
                        <span className={getProfitColor(pond.net_profit)}>
                          {formatCurrency(pond.net_profit)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={getProfitColor(pond.profit_margin)}>
                        {formatPercentage(pond.profit_margin)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={getProfitColor(pond.roi)}>
                        {formatPercentage(pond.roi)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Monthly Trends */}
      {report && report.monthly_trends.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Monthly Trends</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Month
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Income
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expenses
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Profit
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {report.monthly_trends.map((month, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {month.month}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {formatCurrency(month.income)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                      {formatCurrency(month.expenses)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        {getProfitIcon(month.profit)}
                        <span className={getProfitColor(month.profit)}>
                          {formatCurrency(month.profit)}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialReports;
